import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Check,
  X,
  RefreshCw,
  Loader2,
  ShieldCheck,
  Clock,
} from "lucide-react";

const STATUS_LABEL = {
  pending_review: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
};

const STATUS_COLOR = {
  pending_review: "bg-copper/10 text-copper",
  approved: "bg-forest/10 text-forest",
  rejected: "bg-rose-100 text-rose-700",
};

export default function AdminBankTransfers() {
  const [status, setStatus] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bank-transfers?status=${status}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando solicitudes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  const approve = async (req) => {
    if (!window.confirm(`¿Confirmas que ya viste la transferencia de ${req.email} en tu cuenta BBVA?\nEsto desbloqueará su proyecto de inmediato.`)) return;
    setActionId(req.request_id);
    try {
      await api.post(`/admin/bank-transfers/${req.request_id}/approve`);
      toast.success("Transferencia aprobada y proyecto desbloqueado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error aprobando");
    } finally {
      setActionId(null);
    }
  };

  const reject = async (req) => {
    const reason = window.prompt("Motivo del rechazo (será registrado):", "Transferencia no localizada en BBVA");
    if (!reason || reason.trim().length < 3) return;
    setActionId(req.request_id);
    try {
      await api.post(`/admin/bank-transfers/${req.request_id}/reject`, { reason: reason.trim() });
      toast.success("Solicitud rechazada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error rechazando");
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="admin-bank-transfers-page">
      <header className="border-b border-rule bg-paper/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Link to="/" className="inline-flex items-baseline gap-0.5">
              <span className="font-serif italic text-2xl text-copper">una</span><span className="font-serif text-2xl text-ink">TESIS</span>
            </Link>
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-forest/10 text-forest px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-medium">
              <ShieldCheck size={11} /> Admin · Transferencias
            </span>
          </div>
          <Link to="/admin" className="text-[13px] text-ink-soft hover:text-forest inline-flex items-center gap-1.5">
            <ArrowLeft size={13} /> Volver a usuarios
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">Operación</p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink inline-flex items-center gap-2">
              <Building2 size={28} className="text-forest" /> Transferencias BBVA
            </h1>
            <p className="mt-2 text-[13px] text-ink-soft max-w-xl">
              Revisa los folios reportados por estudiantes contra tu estado de cuenta BBVA y aprueba el desbloqueo cuando confirmes el ingreso.
            </p>
          </div>
          <button
            onClick={load}
            data-testid="admin-bt-refresh"
            className="inline-flex items-center gap-2 text-[13px] text-ink-soft hover:text-forest"
          >
            <RefreshCw size={13} /> Refrescar
          </button>
        </div>

        {/* Filter tabs */}
        <div role="tablist" className="mt-6 inline-flex items-center gap-1 rounded-full border border-rule bg-paper-2 p-1">
          {["pending_review", "approved", "rejected"].map((s) => (
            <button
              key={s}
              data-testid={`admin-bt-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`px-4 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                status === s ? "bg-forest text-paper" : "text-ink-soft hover:text-ink"
              }`}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-rule bg-white overflow-hidden">
          {loading ? (
            <div className="px-6 py-16 grid place-items-center text-ink-soft">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="px-6 py-16 text-center text-ink-soft">
              <Clock size={22} className="mx-auto text-forest/60" />
              <p className="mt-3 text-[14px]">
                {status === "pending_review"
                  ? "Sin solicitudes pendientes. Cuando un estudiante notifique una transferencia aparecerá aquí."
                  : "Sin registros en este estado."}
              </p>
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-paper-2 text-ink-soft text-[11px] tracking-[0.16em] uppercase font-medium">
                <tr>
                  <th className="text-left px-4 py-3">Estudiante</th>
                  <th className="text-left px-4 py-3">Folio / Rastreo</th>
                  <th className="text-left px-4 py-3">Proyecto</th>
                  <th className="text-left px-4 py-3">Notificado</th>
                  <th className="text-right px-4 py-3">Acción</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const created = new Date(it.created_at);
                  const isPending = it.status === "pending_review";
                  return (
                    <tr key={it.request_id} className="border-t border-rule/60 align-top" data-testid={`admin-bt-row-${it.request_id}`}>
                      <td className="px-4 py-3">
                        <p className="text-ink font-medium">{it.email}</p>
                        <p className="text-[11px] text-ink-soft/80 mt-0.5">{it.user_id}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-ink font-mono break-all">{it.reference_code}</p>
                        {it.note && <p className="text-[11px] text-ink-soft mt-1 italic">&ldquo;{it.note}&rdquo;</p>}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-ink-soft text-[12px] break-all">{it.project_id}</p>
                      </td>
                      <td className="px-4 py-3 text-ink-soft text-[12px]">
                        {created.toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                        <span className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLOR[it.status]}`}>
                          {STATUS_LABEL[it.status]}
                        </span>
                        {it.rejection_reason && (
                          <p className="mt-1 text-[10.5px] text-rose-700 italic">{it.rejection_reason}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isPending ? (
                          <div className="inline-flex gap-2">
                            <button
                              data-testid={`admin-bt-approve-${it.request_id}`}
                              onClick={() => approve(it)}
                              disabled={actionId === it.request_id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-forest text-paper text-[12px] font-medium hover:bg-forest-dark disabled:opacity-60"
                            >
                              <Check size={12} /> Aprobar
                            </button>
                            <button
                              data-testid={`admin-bt-reject-${it.request_id}`}
                              onClick={() => reject(it)}
                              disabled={actionId === it.request_id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-rule text-ink-soft hover:text-rose-700 hover:border-rose-300 text-[12px] font-medium disabled:opacity-60"
                            >
                              <X size={12} /> Rechazar
                            </button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-ink-soft/70">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
